import { useMutation, useQueryClient } from '@tanstack/react-query'

import { CAR_STORY_QUERY_KEYS } from '../../lib/queryKeys'
import { likeMycleContent, unlikeMycleContent } from '../request/carStoryApis'

import type {
  IMycleContentDetailResponse,
  IMycleContentLikeResponse,
} from '../../model/home'

interface IToggleLikeVariables {
  /** 현재 UI 상의 좋아요 상태 — true 면 unlike, false 면 like 호출 */
  currentLiked: boolean
}

/**
 * 좋아요 토글 후 콘텐츠 상세 캐시(liked/likeCount)를 함께 동기화한다.
 * 동기화하지 않으면 페이지 재진입 시 캐시된 stale 데이터로 좋아요 미반영처럼 보임.
 */
const useToggleMycleContentLikeMutation = (contentId: number) => {
  const queryClient = useQueryClient()

  return useMutation<IMycleContentLikeResponse, Error, IToggleLikeVariables>({
    mutationFn: ({ currentLiked }) =>
      currentLiked
        ? unlikeMycleContent(contentId)
        : likeMycleContent(contentId),
    onSuccess: ({ liked: serverLiked }) => {
      queryClient.setQueryData<IMycleContentDetailResponse>(
        CAR_STORY_QUERY_KEYS.DETAIL.detail(contentId),
        prev => {
          if (!prev) return prev
          if (prev.liked === serverLiked) return prev
          const delta = serverLiked ? 1 : -1
          return {
            ...prev,
            liked: serverLiked,
            likeCount: prev.likeCount + delta,
          }
        }
      )
    },
  })
}

export default useToggleMycleContentLikeMutation
